import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Maximize2 } from "lucide-react";
import { api, resolveImg, formatError, getVisitorId } from "@/lib/api";
import { useT, usePageTitle, Trans } from "@/i18n/LanguageContext";
import ImageLightbox from "@/components/ImageLightbox";

export default function ProductDetail() {
  const t = useT();
  const { id, productSlug, categorySlug } = useParams();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  // Anti-spam: honeypot value + when the form was first shown
  const [honeypot, setHoneypot] = useState("");
  const mountedAt = useRef(Date.now());
  const [related, setRelated] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Product names are entered once and shown as-is; only the fallbacks are translated.
  usePageTitle(product?.name || (notFound ? t("meta.title.notFound") : t("meta.title.collections")));

  useEffect(() => {
    const endpoint = productSlug
      ? `/products/by-slug/${productSlug}`
      : `/products/${id}`;
    setNotFound(false);
    setProduct(null);
    api.get(endpoint)
      .then((r) => { setProduct(r.data); setActive(0); })
      .catch(() => { setProduct(null); setNotFound(true); });
  }, [id, productSlug]);

  // Report a view for the admin counter — once per product per browser session.
  useEffect(() => {
    if (!product?.id) return;
    const key = `viewed-${product.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post(`/products/${product.id}/view`, { visitor_id: getVisitorId() }).catch(() => {});
  }, [product?.id]);

    useEffect(() => {
        if (!product) return;
        api
            .get("/products", { params: { category: product.category_id, visible: true, page_size: 20 } })
            .then((r) => setRelated((r.data.items ?? []).filter((p) => p.id !== product.id).slice(0, 8)))
            .catch(() => {});
    }, [product]);

    const submit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                name: form.name.trim(),
                email: form.email.trim() || undefined,   // nu trimite dacă e gol
                phone: form.phone.trim(),
                message: form.message.trim(),
                kind: "product",
                product_id: product.id,
                product_name: product.name,
                website: honeypot,
                form_time: Date.now() - mountedAt.current,
            });
            toast.success(t("prod.inquire.success"));
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || t("prod.inquire.error"));
        } finally {
            setSubmitting(false);
        }
    };


    // Validare client-side
    const validateForm = () => {
        if (form.name.trim().length < 2) {
            toast.error(t("prod.inquire.nameRequired")); return false;
        }
        if (!/^\+?[\d\s\-().]{7,20}$/.test(form.phone)) {
            toast.error(t("prod.inquire.phoneInvalid")); return false;
        }
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error(t("prod.inquire.emailInvalid")); return false;
        }
        if (form.message.trim().length < 5) {
            toast.error(t("prod.inquire.messageRequired")); return false;
        }
        return true;
    };



    if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="product-not-found">
        <p className="overline">{t("cat.overline")}</p>
        <h1 className="font-serif text-4xl mt-4">{t("prod.notFound.title")}</h1>
        <p className="text-melilux-cream/60 mt-4">{t("prod.notFound.lead")}</p>
        <Link
          to="/collections"
          className="inline-flex items-center gap-2 mt-8 border border-melilux-gold text-melilux-gold px-8 py-3 text-[0.72rem] uppercase tracking-[0.3em] hover:bg-melilux-gold hover:text-melilux-bg transition-colors"
        >
          <ArrowLeft size={14} /> {t("prod.back")}
        </Link>
      </div>
    );
  }

    if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32 text-center">
        <p className="overline">{t("cat.overline")}</p>
        <h1 className="font-serif text-4xl mt-4">{t("prod.loading")}</h1>
      </div>
    );
  }

  const fullImages = (product.images || []).map((src) => resolveImg(src));
  const mainImage = fullImages[active] || fullImages[0];

  return (
    <>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-6" data-testid="product-breadcrumb">
        <Link to="/collections" className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.28em] text-melilux-cream/60 hover:text-melilux-gold transition-colors">
          <ArrowLeft size={14} /> {t("prod.back")}
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 md:gap-x-10 gap-y-12 pb-24" data-testid="product-detail">
        {/* Gallery */}
        <div className="col-span-12 md:col-span-7">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            data-testid="product-main-image"
            className="group relative w-full min-h-[320px] bg-melilux-surface block overflow-hidden cursor-zoom-in"
            aria-label="Open lightbox"
          >
            {/* Shown uncropped — the product page always gets the full original photo. */}
            {mainImage && (
              <img
                src={mainImage}
                alt={product.name}
                className="w-full h-auto max-h-[85vh] object-contain transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              />
            )}
          {product.sale_percentage && (
              <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-melilux-gold z-10">
                  <span className="text-[0.9rem] uppercase tracking-[0.35em] text-melilux-bg/70">Sale</span>
                  <span className="text-[0.9rem] uppercase tracking-[0.35em] text-melilux-bg font-medium">−{product.sale_percentage}%</span>
              </div>
          )}
            <div className="absolute inset-0 bg-melilux-bg/0 group-hover:bg-melilux-bg/15 transition-colors duration-500" />
            <div className="absolute bottom-4 right-4 flex items-center gap-2 px-3 py-2 bg-melilux-bg/70 backdrop-blur border border-white/15 text-[0.6rem] uppercase tracking-[0.25em] text-melilux-cream opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 size={12} strokeWidth={1.5} />
              {t("prod.zoomHint")}
            </div>
          </button>

          {product.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3" data-testid="product-thumbnails">
              {product.images.map((src, idx) => (
                <button
                  key={idx}
                  onClick={() => setActive(idx)}
                  data-testid={`thumb-${idx}`}
                  className={`aspect-square overflow-hidden ${
                    active === idx ? "ring-1 ring-melilux-gold" : "opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={resolveImg(src, 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="col-span-12 md:col-span-5 md:sticky md:top-28 md:self-start">
          <p className="overline" data-testid="product-category">{product.category_name || product.room}</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-4 leading-[0.95]" data-testid="product-name">
            {product.name}
          </h1>
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            <span className="text-[0.7rem] uppercase tracking-[0.28em] text-melilux-gold" data-testid="product-status">
              {product.status === "Limited" ? t("prod.limited") : product.status}
            </span>
            {product.style && (
              <span className="text-[0.7rem] uppercase tracking-[0.28em] text-melilux-cream/55 border-l border-white/15 pl-4" data-testid="product-style">
                {product.style}
              </span>
            )}
          </div>

          <p className="text-melilux-cream/70 mt-8 leading-relaxed" data-testid="product-description">
            {product.description}
          </p>

          <div className="mt-10 space-y-6 border-t border-white/10 pt-8">
              {product.price && (
                  <Spec
                      label={t("prod.price")}
                      value={
                          product.sale_price && product.sale_price !== product.price ? (
                              <span className="flex items-baseline gap-3 flex-wrap">
                              <span className="line-through opacity-40">{product.price}</span>
                              <span className="text-melilux-gold">{product.sale_price}</span>
                            </span>
                          ) : product.price
                      }
                  />
              )}
            {product.dimensions && <Spec label={t("prod.dimensions")} value={product.dimensions} />}
            {product.materials?.length > 0 && <Spec label={t("prod.materials")} value={product.materials.join(" · ")} />}
            {product.care_instructions && <Spec label={t("prod.care")} value={product.care_instructions} />}
            {product.room && <Spec label={t("prod.room")} value={product.room} />}
          </div>

          {/* Inquire form */}
          <div className="mt-12 border-t border-white/10 pt-10" data-testid="inquire-form">
            <p className="overline">{t("prod.inquire.overline")}</p>
            <h3 className="font-serif text-2xl mt-3">
              <Trans tKey="prod.inquire.title" />
            </h3>

            <form onSubmit={submit} className="mt-7 space-y-5">
                {/* Honeypot — offscreen, humans never see or fill it */}
                <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
                <input
                    type="text"
                    placeholder={t("prod.inquire.name") + " *"}
                    className="editorial-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    maxLength={100}
                    required
                />
                <input
                    type="tel"
                    placeholder={t("prod.inquire.phone") + " *"}
                    className="editorial-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    maxLength={20}
                    required
                />
                <input
                    type="email"
                    placeholder={t("prod.inquire.email")}   // fără *
                    className="editorial-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    maxLength={150}
                />
                <textarea
                    placeholder={t("prod.inquire.message") + " *"}
                    className="editorial-input"
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    maxLength={2000}
                    required
                />
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="inquire-submit"
                    className="mt-2 bg-melilux-gold text-melilux-bg px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] hover:opacity-80 transition-opacity disabled:opacity-60"
                >
                    {submitting ? t("prod.inquire.sending") : t("prod.inquire.submit")}
                </button>
            </form>
          </div>
        </div>
      </section>

        {/* Related */}
        {related.length > 0 && (
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-white/10" data-testid="related-section">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
                    <div>
                        <p className="overline">{t("prod.related.overline")}</p>
                        <h2 className="font-serif text-3xl md:text-5xl mt-3">{t("prod.related.title")}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => document.getElementById('related-scroll').scrollBy({ left: -320, behavior: 'smooth' })}
                            className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-melilux-gold hover:text-melilux-gold transition-colors"
                        >
                            ←
                        </button>
                        <button
                            onClick={() => document.getElementById('related-scroll').scrollBy({ left: 320, behavior: 'smooth' })}
                            className="w-10 h-10 border border-white/15 flex items-center justify-center hover:border-melilux-gold hover:text-melilux-gold transition-colors"
                        >
                            →
                        </button>
                    </div>
                </div>
                <div
                    id="related-scroll"
                    className="flex gap-6 overflow-x-auto scrollbar-hide pb-2"
                >
                    {related.map((p) => (
                        <Link
                            key={p.id}
                            to={`/collections/${p.category_slug}/${p.slug}`}
                            className="group shrink-0 w-64"
                            data-testid={`related-${p.id}`}
                        >
                            <div className="hover-zoom aspect-[3/4] bg-melilux-surface overflow-hidden">
                                {(p.card_image || p.images?.[0]) && (
                                    <img src={resolveImg(p.card_image || p.images[0], 480)} loading="lazy" decoding="async" alt={p.name} className="w-full h-full object-cover" />
                                )}
                            </div>
                            <div className="mt-4">
                                <p className="overline">{p.category_name || p.room}</p>
                                <h3 className="font-serif text-xl mt-2 text-melilux-cream group-hover:text-melilux-gold transition-colors">{p.name}</h3>
                                {p.price && (
                                    <p className="mt-1 text-sm text-melilux-cream/55 tracking-wide">
                                        {p.currency === "MDL" ? `${p.price} MDL` : `€${p.price}`}
                                    </p>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        )}

      {/* Lightbox */}
      {lightboxOpen && fullImages.length > 0 && (
        <ImageLightbox
          images={fullImages}
          startIndex={active}
          alt={product.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}

function Spec({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-6">
      <div className="overline col-span-1">{label}</div>
      <div className="col-span-2 text-melilux-cream/85 text-sm leading-relaxed">{value}</div>
    </div>
  );
}
